/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import Action, { ActionPayload } from './Action'
import Face, { FacePayload } from './Face'

export type Change = {
  operation: 'create' | 'update' | 'delete'
  template: string
  id: string
  data: FacePayload | ActionPayload
}

export interface Policy {
  templateVariation: string
}

export interface StudioInfo {
  businessId: string
  blueprintId: string
  campaignId: string
  objectDefinitionId: string
}

export type ImagePolicy = {
  count_max?: number
  field?: string
  value?: string | number | boolean
  resource?: string
}

export enum ListingStatus {
  Unlisted = 'unlisted',
  Active = 'active',
  Pending = 'pending',
  Failed = 'failed',
  Unknown = ''
}

export enum SellChannel {
  Marketplace = 'marketplace',
  Direct = 'direct',
  None = 'none'
}

export type CommerceInfo = {
  currency: string
  status?: ListingStatus
  channel?: SellChannel
  price: number | string
  error?: string
  checkoutUrl?: string
  productUrl?: string
  saleDate?: string
}

export interface PrivateData {
  behaviors?: string[]
  'studio-info-v1'?: StudioInfo
  'commerce-v1'?: CommerceInfo
  'numbered-v1'?: {
    name?: string
    number: number
  }
  'scarcity-v1'?: {
    maxObjects: number
  }
  'lifecycle-v1'?: {
    initialized: boolean
    supportsRefresh: boolean
  }
  'share-v1'?: {
    scores: {
      clicks: number
      engagements: number
      influencers: number
      networkSpend: number
      shares: number
      spend: number
    }
    shareTargetUri: 'varius.object-definition:varius.io:this' | string
    shareTargetUrl: string
    shareUrl: string
  }
  'cloneable-v1'?: {
    cloningScore: number
    numDirectClones: number
  }
  'mutable-v1'?: {
    title?: string
    description?: string
    category?: string
    resources?: Resource[]
  }
  'royalties-v1'?: {
    artist: number
  }
  // This is deprecated but still needs to be dealt with
  state?: {
    'varius.behavior:varius.io:studio-info-v1': {
      businessId: string
      blueprintUri: string
      campaignUri: string
      objectDefinitionUri: string
    }
  }
  analytics_data?: {
    objectDefinitionId?: string
  }

  // deprecated
  image_policy?: ImagePolicy[]
  padding_start?: number
  padding_end?: number
  direction?: string
  unlock_key?: string
  resources?: SimpleResource[]
}

// Not sure what this is of if still valid
type SimpleResource = {
  name: string
  value: string
}

export type VatomResource = {
  name: string
  resourceType: string
  value: {
    value: string
  }
}

export type Activity = {
  date: string
  name: string
  price: string
  initiatorId: string
  recipientId: string
}

// TODO: I think there is a BV structure we can borrow that has VR coords as well
export type Position = {
  type: 'Point'
  coordinates: number[]
}

export interface VatomPayload {
  id: string
  private: PrivateData
  unpublished?: boolean
  version: string
  sync: number
  when_created: string
  when_modified?: string
  'vAtom::vAtomType': {
    title: string
    parent_id: string
    owner: string
    author: string
    transferred_by: string
    description: string
    category: string
    publisher_fqdn: string
    root_type: string
    child_policy?: [
      {
        template_variation: string
        creation_policy: {
          policy_count_max: number
          enforce_policy_count_max: number
        }
      }
    ]
    template: string
    template_variation: string
    resources: VatomResource[]
    cloning_score?: number
    padding_start?: number

    // deprecated
    icon_stages?: ImagePolicy[]
    image_mode?: string
    activate_action?: string
    num_direct_clones?: number
    dropped: boolean
    geo_pos?: Position
    animation_rules: any[]
    is_map_dispenser: boolean
  }
  eth?: {
    network: string
    contract: string
    emitted: boolean
  }
  chain?: {
    network: string
    contract: string
    tx_id: string
    token_id: string
    minted: boolean
  }
}

export type ARTransform = {
  scale: {
    x: number
    y: number
    z: number
  }
  translate: {
    x: number
    y: number
    z: number
  }
  rotate: {
    x: number
    y: number
    z: number
  }
}

export type Resource = {
  name: string
  url?: string
  type: string
  ar_transform?: ARTransform
  animation_rules?: any
  auto_rotate?: boolean
}

export type AnimationRule = {
  on: string
  play: string
  target: string
  value: string
}

export type ERC721Metadata = {
  name: string
  description: string
  image?: string

  // OpenSea additions
  external_uri?: string
  attributes: [
    {
      trait_type: string
      value: number | string
    }
  ]

  // OpenSea specific
  background_color?: string
  animation_url?: string
  youtube_url?: string

  contract_data?: ERC721ContractMetadata

  // category: string;
  // owner: string;
  // author: string;
  // businessId: string;
  // edition: string;
}

export type ERC721ContractMetadata = {
  name: string
  description: string
  image: string
  external_link: string
  seller_fee_basis_points: number
  fee_recipient: string
}

export default class Vatom {
  payload: VatomPayload
  vatomActions: Action[]
  vatomFaces: Face[]
  tokenId: string

  constructor(payload: VatomPayload, faces: Face[], actions: Action[]) {
    this.payload = payload
    this.vatomActions = actions
    this.vatomFaces = faces

    this.tokenId = this.generateTokenId()
  }

  get id(): string {
    return this.payload.id
  }

  // Should probably return vatom user id??
  get owner(): string {
    return this.properties.owner
  }

  get author(): string {
    return this.properties.author
  }

  get lastOwner(): string {
    return this.properties.transferred_by
  }

  get parentId(): string {
    return this.properties.parent_id
  }

  get private() {
    return this.payload.private
  }

  // get unpublished() {
  // 	return this.payload.unpublished;
  // }

  // get version() {
  // 	return this.payload.version;
  // }

  get sync() {
    return this.payload.sync
  }

  get created(): Date {
    return new Date(this.payload.when_created)
  }

  get modified() {
    return this.payload.when_modified ? new Date(this.payload.when_modified) : this.created
  }

  get properties() {
    return this.payload['vAtom::vAtomType' as const]
  }

  /** True if this is a folder vatom */
  get isFolder() {
    return this.properties.root_type.indexOf('ContainerType') !== -1
  }

  /** True if this is a defined folder vatom, ie a folder that can only accept certain types of child vatoms. */
  get isDefinedFolder() {
    return this.properties.root_type.indexOf('DefinedFolderContainerType') !== -1
  }

  /** True if this is a discover folder vatom, ie a folder whose contents are fetched by performing the `Discover` action on it. */
  get isDiscoverFolder() {
    return this.properties.root_type.indexOf('DiscoverFolderContainerType') !== -1
  }

  async canCombineWith(otherVatom: Vatom): Promise<boolean> {
    // Check if the faces support combine

    // this.vatomFaces.find(f => f.canCombineWith)
    // Stop if null or ourselves
    if (!otherVatom || this.id === otherVatom.id) {
      return false
    }

    // If it's not a folder, deny
    if (!this.isFolder) {
      return false
    }

    // If it's not a defined folder, allow
    if (!this.isDefinedFolder) {
      return true
    }

    // Get child policies
    const child_policies = this.properties.child_policy || []

    // Make child policies a little easier for us to understand
    const policies: Policy[] = child_policies.map(p => ({
      templateVariation: p.template_variation,
      maxCount: (p.creation_policy && p.creation_policy.policy_count_max) || 9999,
      enforceMaxCount: (p.creation_policy && p.creation_policy.enforce_policy_count_max) || false
    }))

    // Make sure we have a match
    for (const policy of policies) {
      // Check if template variation matches
      if (policy.templateVariation === otherVatom.properties.template_variation) {
        return true
      }
    }

    // No match found, deny
    return false
  }

  private generateTokenId() {
    const hex = this.id.replace(/-/g, '')

    function add(x: any, y: any) {
      let c = 0
      const r = []
      x = x.split('').map(Number)
      y = y.split('').map(Number)
      while (x.length || y.length) {
        const s = (x.pop() || 0) + (y.pop() || 0) + c
        r.unshift(s < 10 ? s : s - 10)
        c = s < 10 ? 0 : 1
      }
      if (c) r.unshift(c)
      return r.join('')
    }

    let dec = '0'
    hex.split('').forEach(function (chr) {
      const n = parseInt(chr, 16)
      for (let t = 8; t; t >>= 1) {
        dec = add(dec, dec)
        if (n & t) dec = add(dec, '1')
      }
    })
    return dec
  }

  get editionInfo() {
    const numberedBehavior = this.private['numbered-v1']

    if (!numberedBehavior) {
      return undefined
    }

    const numbered = this.private['lifecycle-v1']?.initialized ? numberedBehavior.number : 0
    const scarcity = this.private['scarcity-v1']?.maxObjects || Infinity

    return {
      numbered,
      scarcity
    }
  }

  isBusinessFolder() {
    return this.private['studio-info-v1']?.blueprintId === 'business-folder-v1'
  }

  // onCardStateChange = async (name: string, payload: any) => {
  // 	// TODO
  // };

  // get icon() {

  // 	let icon: string | undefined;
  // 	// Check vatom face
  // 	let face = FaceSelection.Icon(this);
  // 	if (
  // 		face &&
  // 		face.properties.display_url.toLowerCase() === "native://image-policy"
  // 	) {
  // 		// Use the Image Policy logic to select the image to display
  // 		icon = ImagePolicyFace.imageURL(this.vatomNet, this, face);
  // 		if (icon) icon = this.vatomNet.UserManager.encodeAssetProvider(icon);
  // 	} else {
  // 		// Use plain ActivatedImage
  // 		var iconRes = this.properties.resources.find(
  // 			(r) => r.name === "ActivatedImage"
  // 		);
  // 		icon =
  // 			iconRes &&
  // 			this.vatomNet.UserManager.encodeAssetProvider(iconRes.value.value);
  // 	}
  // 	return icon;
  // }

  get blockchainInfo() {
    if (!this.payload.eth && !this.payload.chain) return undefined

    const chain = (this.payload.eth || this.payload.chain)!
    const network = chain.network

    let contractAddress = chain.contract
    if (chain.contract === 'bv-vatom-v1')
      contractAddress = '0x210e74c878d96c4aa1e9cbaf32fe1950dee2ca53'

    // const { businessId } = this.getStudioInfo();
    // if (businessId === "PoIvoqVCm3")
    // 	// This is a hack because the contract is not appearing in the vatom eth section - only the Template
    // 	contractAddress = "0x26D1319C38B3979CFA062146245Ccb3424Bb4B53";

    return {
      tokenId: this.tokenId,
      network,
      // networkName: this.getNetworkName(network),
      // networkIcon: this.getNetworkIcon(network),
      contractAddress,
      owner: 'unknown' // Need to perform a reverse lookup?
      // tokenLink: isMinted ? this.getTokenLink(network, contractAddress) : undefined
    }
  }
}
